import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  NativeSelect,
  NumberInput,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconRefresh } from '@/components/icons'
import {
  DataTable,
  DataTableScroll,
  DataTbody,
  DataTd,
  DataTh,
  DataThead,
  DataTr,
  EmptyState,
  PageCenterLoader,
} from '@/components/ui'
import { api, ApiError } from '@/lib/api'
import type { ChannelRow, ProjectRow } from '@/types/api'

type EmailBackfillResult = {
  channel_id: string
  applied: boolean
  date_from: string
  date_to: string
  found: number
  selected: number
  processed: number
  batch_size: number
  batches: number
  sent: number
  failed: number
  duplicates: number
  samples: Array<{
    seq?: string | null
    from?: string | null
    subject?: string | null
    message_id?: string | null
  }>
  errors: Array<{ seq?: string | null; status?: number; error: string }>
}

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10)
}

function daysAgoIsoDate(days: number) {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString().slice(0, 10)
}

export function DebugEmailBackfillTab() {
  const [projects, setProjects] = useState<ProjectRow[]>([])
  const [channels, setChannels] = useState<ChannelRow[]>([])
  const [projectId, setProjectId] = useState('')
  const [channelId, setChannelId] = useState('')
  const [dateFrom, setDateFrom] = useState(daysAgoIsoDate(1))
  const [dateTo, setDateTo] = useState(todayIsoDate())
  const [limit, setLimit] = useState(0)
  const [batchSize, setBatchSize] = useState(250)
  const [loadingProjects, setLoadingProjects] = useState(false)
  const [loadingChannels, setLoadingChannels] = useState(false)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<EmailBackfillResult | null>(null)

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true)
    try {
      const rows = await api.projects()
      setProjects(rows)
      if (!projectId && rows[0]) setProjectId(rows[0].id)
    } catch (e) {
      showNotification({
        title: 'Проекты',
        message: e instanceof ApiError ? e.message : 'Не удалось загрузить проекты',
        color: 'red',
      })
    } finally {
      setLoadingProjects(false)
    }
  }, [projectId])

  const loadChannels = useCallback(async (pid: string) => {
    if (!pid) {
      setChannels([])
      setChannelId('')
      return
    }
    setLoadingChannels(true)
    try {
      const rows = await api.channelsList(pid)
      const emailRows = rows.filter((row) => String(row.channel_type || '').toLowerCase() === 'email')
      setChannels(emailRows)
      setChannelId((current) => {
        if (current && emailRows.some((row) => row.id === current)) return current
        return emailRows[0]?.id || ''
      })
    } catch (e) {
      showNotification({
        title: 'Каналы',
        message: e instanceof ApiError ? e.message : 'Не удалось загрузить email-каналы',
        color: 'red',
      })
    } finally {
      setLoadingChannels(false)
    }
  }, [])

  useEffect(() => {
    void loadProjects()
  }, [loadProjects])

  useEffect(() => {
    void loadChannels(projectId)
    setResult(null)
  }, [loadChannels, projectId])

  const projectOptions = useMemo(
    () => projects.map((p) => ({ value: p.id, label: p.name || p.slug || p.id })),
    [projects],
  )

  const channelOptions = useMemo(
    () =>
      channels.map((c) => ({
        value: c.id,
        label: `${c.name || c.id}${c.is_active === false ? ' (выключен)' : ''}`,
      })),
    [channels],
  )

  const run = useCallback(
    async (apply: boolean) => {
      if (!channelId) {
        showNotification({ title: 'Канал не выбран', message: 'Выберите email-канал.', color: 'yellow' })
        return
      }
      if (!dateFrom) {
        showNotification({ title: 'Дата не указана', message: 'Укажите дату начала.', color: 'yellow' })
        return
      }
      setBusy(true)
      try {
        const res = await api.channelEmailBackfill(channelId, {
          date_from: dateFrom,
          date_to: dateTo || null,
          apply,
          limit,
          batch_size: batchSize,
        })
        setResult(res)
        showNotification({
          title: apply ? 'Догрузка писем завершена' : 'Проверка писем завершена',
          message: apply
            ? `Найдено: ${res.found}, отправлено: ${res.sent}, дублей: ${res.duplicates}, ошибок: ${res.failed}`
            : `Найдено писем: ${res.found}, выбрано: ${res.selected}`,
          color: res.failed > 0 ? 'yellow' : 'green',
        })
      } catch (e) {
        showNotification({
          title: apply ? 'Догрузка писем' : 'Проверка писем',
          message: e instanceof ApiError ? e.message : 'Не удалось выполнить операцию',
          color: 'red',
        })
      } finally {
        setBusy(false)
      }
    },
    [batchSize, channelId, dateFrom, dateTo, limit],
  )

  return (
    <Stack gap="sm">
      <Alert color="yellow" variant="light">
        <Text size="sm">
          Сначала выполните проверку без записи. Догрузка отправляет письма через обычный email webhook; повторные письма отсекаются по Message-ID.
        </Text>
      </Alert>

      <Card withBorder padding="sm" radius="md">
        <Stack gap="sm">
          <Group align="end" wrap="wrap">
            <NativeSelect
              label="Проект"
              value={projectId}
              data={projectOptions}
              disabled={loadingProjects || busy}
              onChange={(e) => setProjectId(e.currentTarget.value)}
              w={260}
            />
            <NativeSelect
              label="Email-канал"
              value={channelId}
              data={channelOptions.length ? channelOptions : [{ value: '', label: 'Email-каналов нет' }]}
              disabled={loadingChannels || busy || channelOptions.length === 0}
              onChange={(e) => setChannelId(e.currentTarget.value)}
              w={260}
            />
            <TextInput
              label="С даты"
              type="date"
              value={dateFrom}
              disabled={busy}
              onChange={(e) => setDateFrom(e.currentTarget.value)}
              w={150}
            />
            <TextInput
              label="По дату"
              type="date"
              value={dateTo}
              disabled={busy}
              onChange={(e) => setDateTo(e.currentTarget.value)}
              w={150}
            />
            <NumberInput
              label="Макс. писем"
              description="0 = все"
              min={0}
              max={100000}
              value={limit}
              disabled={busy}
              onChange={(value) => setLimit(typeof value === 'number' ? value : 0)}
              w={130}
            />
            <NumberInput
              label="Пачка"
              min={1}
              max={1000}
              value={batchSize}
              disabled={busy}
              onChange={(value) => setBatchSize(typeof value === 'number' ? value : 250)}
              w={110}
            />
            <Button
              variant="default"
              leftSection={<IconRefresh size={14} />}
              loading={busy}
              disabled={!channelId}
              onClick={() => void run(false)}
            >
              Проверить
            </Button>
            <Button color="orange" loading={busy} disabled={!channelId} onClick={() => void run(true)}>
              Догрузить письма
            </Button>
          </Group>
        </Stack>
      </Card>

      {busy ? <PageCenterLoader label="Обработка писем" minHeight={140} /> : null}

      {result ? (
        <Card withBorder padding="sm" radius="md">
          <Stack gap="sm">
            <Group gap="xs" wrap="wrap">
              <Badge variant="light" color={result.applied ? 'orange' : 'blue'}>
                {result.applied ? 'Запись' : 'Проверка'}
              </Badge>
              <Badge variant="light">Найдено: {result.found}</Badge>
              <Badge variant="light">Выбрано: {result.selected}</Badge>
              <Badge variant="light">Обработано: {result.processed}</Badge>
              <Badge variant="light">Пачек: {result.batches}</Badge>
              <Badge variant="light" color="green">
                Отправлено: {result.sent}
              </Badge>
              <Badge variant="light" color="gray">
                Дубли: {result.duplicates}
              </Badge>
              <Badge variant="light" color={result.failed ? 'red' : 'gray'}>
                Ошибки: {result.failed}
              </Badge>
            </Group>

            {result.samples.length ? (
              <DataTableScroll>
                <DataTable>
                  <DataThead>
                    <DataTr>
                      <DataTh>SEQ</DataTh>
                      <DataTh>От кого</DataTh>
                      <DataTh>Тема</DataTh>
                      <DataTh>Message-ID</DataTh>
                    </DataTr>
                  </DataThead>
                  <DataTbody>
                    {result.samples.map((item, idx) => (
                      <DataTr key={`${item.seq || idx}-${item.message_id || idx}`}>
                        <DataTd>{item.seq || '—'}</DataTd>
                        <DataTd>
                          <Text size="xs" lineClamp={1}>{item.from || '—'}</Text>
                        </DataTd>
                        <DataTd>
                          <Text size="xs" lineClamp={2}>{item.subject || '—'}</Text>
                        </DataTd>
                        <DataTd>
                          <Text size="xs" lineClamp={1}>{item.message_id || '—'}</Text>
                        </DataTd>
                      </DataTr>
                    ))}
                  </DataTbody>
                </DataTable>
              </DataTableScroll>
            ) : (
              <EmptyState title="Писем нет" description="В выбранном диапазоне email-service не нашёл писем." />
            )}

            {result.errors.length ? (
              <Alert color="red" variant="light">
                <Stack gap={4}>
                  {result.errors.slice(0, 10).map((err, idx) => (
                    <Text key={`${err.seq || idx}-${err.status || ''}`} size="xs">
                      {err.seq || '—'} {err.status ? `HTTP ${err.status}` : ''}: {err.error}
                    </Text>
                  ))}
                </Stack>
              </Alert>
            ) : null}
          </Stack>
        </Card>
      ) : null}
    </Stack>
  )
}
